import { LinkBreakIcon } from '@phosphor-icons/react';

import { Button } from 'components/button/Button';
import { SpinnerBlock } from 'components/Spinner';
import { useTranslation } from 'contexts/translation-context';

import { type TelemetryVehicle } from '../models';
import { labelOf, linkedNameOf } from '../vehicle-labels';

export interface IVehicleLinkListProps {
  vehicles: TelemetryVehicle[];
  isLoading?: boolean;
  /** The row whose own action is running: it says so, and cannot be submitted twice. */
  pendingId?: string | null;
  onPick: (vehicle: TelemetryVehicle) => void;
  /** Rows this predicate accepts get an unlink button. Left out, none do. */
  canUnlink?: (vehicle: TelemetryVehicle) => boolean;
  onUnlink?: (vehicle: TelemetryVehicle) => void;
}

/**
 * The reported units, one row each, with where they are linked now.
 *
 * Shared by the two dialogs that link them: the tracking settings start from a unit and ask for its
 * target, the trailer page starts from a trailer and asks for its unit. The rows are the same either way,
 * and so is the fact that a unit is linked to exactly one of a truck or a trailer.
 */
export const VehicleLinkList = ({
  vehicles,
  isLoading,
  pendingId,
  onPick,
  canUnlink,
  onUnlink,
}: IVehicleLinkListProps) => {
  const { i18n } = useTranslation();

  if (isLoading && vehicles.length === 0) {
    return <SpinnerBlock message={i18n('liveTracking.vehiclesLoading')} className="min-h-20" />;
  }

  if (vehicles.length === 0) {
    return <p className="text-sm text-fg-muted">{i18n('liveTracking.noUnits')}</p>;
  }

  return (
    <ul className="divide-y divide-border">
      {vehicles.map((vehicle) => {
        const isLinked = Boolean(vehicle.supplierTruckId || vehicle.trailerId);
        const linkedName = linkedNameOf(vehicle);

        return (
          <li key={vehicle.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
            {/* A raw button rather than the design system's `Button`: this is a whole list row and a
                36px control is the wrong shape for one. */}
            <button
              type="button"
              onClick={() => onPick(vehicle)}
              disabled={pendingId === vehicle.id}
              className="-mx-2 min-w-0 cursor-pointer rounded-md px-2 py-1 text-left transition-colors hover:bg-surface-hover disabled:cursor-progress"
            >
              <div className="text-sm font-medium text-fg">{labelOf(vehicle)}</div>
              <div className="text-sm text-fg-muted">
                {isLinked ? i18n('liveTracking.linkedTo', { truck: linkedName }) : i18n('liveTracking.notLinked')}
              </div>
            </button>

            {onUnlink && canUnlink?.(vehicle) && (
              <Button
                color="ghost"
                size={8}
                isLoading={pendingId === vehicle.id}
                iconLeft={<LinkBreakIcon className="button-icon" />}
                onTrigger={() => onUnlink(vehicle)}
              >
                {i18n('liveTracking.unlink')}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
};
